import { Pressable, StyleSheet, Text, View, type PressableStateCallbackType } from 'react-native';
import { Icon } from 'react-native-paper';

import { NotificationDot } from '@/components/atoms/indicator';
import { colorSemantic, iconSize, rippleOnSurface, spacing } from '@/theme';

const RIPPLE_CONFIG = { color: rippleOnSurface };

export interface SelectOptionRowProps {
  readonly label: string;
  readonly selected: boolean;
  readonly onPress: () => void;
  readonly showIndicator?: boolean;
  readonly indicatorColor?: string;
  readonly indicatorPosition?: 'leading' | 'trailing';
  readonly indicatorSize?: number;
  readonly showDivider?: boolean;
  readonly testID?: string;
}

export const SelectOptionRow = ({
  label,
  selected,
  onPress,
  showIndicator = false,
  indicatorColor = colorSemantic.colorIconErrorStrong,
  indicatorPosition = 'trailing',
  indicatorSize,
  showDivider = true,
  testID,
}: SelectOptionRowProps) => {
  const getPressableStyle = ({ pressed }: PressableStateCallbackType) => [
    styles.row,
    showDivider ? styles.divider : null,
    selected ? styles.selected : null,
    pressed ? styles.pressed : null,
  ];

  const indicator = showIndicator ? (
    <NotificationDot
      color={indicatorColor}
      size={indicatorSize}
      testID={testID ? `${testID}-indicator` : undefined}
    />
  ) : null;

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityState={{ selected }}
      android_ripple={RIPPLE_CONFIG}
      onPress={onPress}
      style={getPressableStyle}
      testID={testID}>
      <View style={styles.labelGroup}>
        {indicatorPosition === 'leading' ? indicator : null}
        <Text style={styles.label}>{label}</Text>
        {indicatorPosition === 'trailing' ? indicator : null}
      </View>
      {selected ? (
        <View testID={testID ? `${testID}-check` : undefined}>
          <Icon color={colorSemantic.colorIconBrandStrong} size={iconSize.md} source="check" />
        </View>
      ) : null}
    </Pressable>
  );
};

const styles = StyleSheet.create({
  row: {
    alignItems: 'center',
    columnGap: spacing[2],
    flexDirection: 'row',
    justifyContent: 'space-between',
    minHeight: 56,
    paddingHorizontal: spacing[4],
    paddingVertical: spacing[2],
  },
  divider: {
    borderBottomColor: colorSemantic.colorBorderNdBase,
    borderBottomWidth: 1,
  },
  // Static token, not the runtime Paper theme: bottom sheets are portalled above
  // PaperProvider, so useTheme() there falls back to the default MD3 palette.
  selected: {
    backgroundColor: colorSemantic.colorBgBrandWeak,
  },
  pressed: {
    opacity: 0.6,
  },
  labelGroup: {
    alignItems: 'center',
    columnGap: spacing[2],
    flexDirection: 'row',
    flexShrink: 1,
  },
  label: {
    color: colorSemantic.colorTextNdPrimary,
    flexShrink: 1,
    fontSize: 16,
    letterSpacing: 0.5,
    lineHeight: 24,
  },
});
